import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Vendor, MSA, SOW, BillingEntry } from '../types';
import { vendorStore, msaStore, sowStore, billingStore, notificationStore } from '../services/store';
import { useVendorRole } from '../hooks/useVendorRole';

function fmt(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);
}

function daysUntil(dateStr: string): number {
  return Math.floor((new Date(dateStr).getTime() - Date.now()) / 86_400_000);
}

export default function DashboardPage() {
  const { role, canSeeVendor } = useVendorRole();
  const [vendors,  setVendors]  = useState<Vendor[]>([]);
  const [msas,     setMsas]     = useState<MSA[]>([]);
  const [sows,     setSows]     = useState<SOW[]>([]);
  const [billing,  setBilling]  = useState<BillingEntry[]>([]);
  const [unread,   setUnread]   = useState(0);

  useEffect(() => {
    const v = vendorStore.list();
    setVendors(v);
    setMsas(msaStore.list());
    setSows(sowStore.list());
    setBilling(billingStore.list());
    setUnread(notificationStore.unreadCount());
  }, []);

  const visibleVendors = vendors.filter(v => canSeeVendor(v.categories));

  const stats = useMemo(() => {
    const active    = visibleVendors.filter(v => v.status === 'Active').length;
    const pending   = visibleVendors.filter(v => v.status === 'Pending').length;
    const inactive  = visibleVendors.filter(v => v.status === 'Inactive').length;
    const archived  = visibleVendors.filter(v => v.status === 'Archived').length;
    const totalSpend = billing.filter(e => e.authStatus === 'Approved').reduce((s, e) => s + e.amount, 0);
    const pendingApprovals =
      billing.filter(e => e.authStatus === 'PendingDM').length +
      msas.filter(m => m.authStatus === 'PendingDM' || m.authStatus === 'PendingTee').length +
      sows.filter(s => s.authStatus === 'PendingDM' || s.authStatus === 'PendingTee').length;
    return { active, pending, inactive, archived, totalSpend, pendingApprovals };
  }, [visibleVendors, billing, msas, sows]);

  type ExpiringItem = { label: string; vendorName: string; days: number; type: string };
  const expiring = useMemo((): ExpiringItem[] => {
    const vendorMap = new Map(vendors.map(v => [v.id, v.name]));
    const items: ExpiringItem[] = [];
    msas.forEach(m => {
      if (!m.expirationDate || m.authStatus !== 'Executed') return;
      const days = daysUntil(m.expirationDate);
      if (days >= 0 && days <= 90) items.push({ label: `MSA (eff. ${m.effectiveDate ?? 'TBD'})`, vendorName: vendorMap.get(m.vendorId) ?? '?', days, type: 'MSA' });
    });
    sows.forEach(s => {
      if (!s.endDate || s.authStatus !== 'Executed') return;
      const days = daysUntil(s.endDate);
      if (days >= 0 && days <= 90) items.push({ label: `${s.sowNumber} — ${s.name}`, vendorName: vendorMap.get(s.vendorId) ?? '?', days, type: 'SOW' });
    });
    return items.sort((a, b) => a.days - b.days);
  }, [msas, sows, vendors]);

  const pendingBilling = useMemo(() =>
    billing.filter(e => e.authStatus === 'PendingDM').slice(0, 5),
  [billing]);

  const statCards = [
    { label: 'Active vendors',     value: String(stats.active),               color: '#3B6D11', to: '/' },
    { label: 'Pending vendors',    value: String(stats.pending),              color: '#854F0B', to: '/?status=Pending' },
    { label: 'Pending approvals',  value: String(stats.pendingApprovals),     color: '#185FA5', to: '/contracts' },
    { label: 'Total approved spend', value: fmt(stats.totalSpend),            color: '#534AB7', to: '/billing' },
    { label: 'Expiring ≤90 days',  value: String(expiring.length),            color: expiring.length > 0 ? '#A32D2D' : '#5F5E5A', to: '/reports' },
    { label: 'Unread alerts',      value: String(unread),                     color: unread > 0 ? '#A32D2D' : '#5F5E5A', to: '/notifications' },
  ];

  const isEmpty = vendors.length === 0;

  return (
    <div className="p-8">
      <h1 className="text-[22px] font-semibold text-gray-900 mb-1">Dashboard</h1>
      <p className="text-sm text-gray-500 mb-6">Christmas Lumber — Vendor Management · Role: {role}</p>

      {isEmpty ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4">🌲</div>
          <h2 className="text-lg font-semibold text-gray-700 mb-2">Welcome to Vendor Management</h2>
          <p className="text-sm text-gray-500 mb-6">Start by adding your first vendor.</p>
          <Link to="/" className="px-4 py-2 bg-[#1a5c2a] text-white rounded-lg text-sm font-medium hover:bg-[#154a22] no-underline">
            Go to Vendor List
          </Link>
        </div>
      ) : (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-3 gap-3.5 mb-7">
            {statCards.map(card => (
              <Link
                key={card.label}
                to={card.to}
                className="bg-white rounded-xl px-5 py-4 shadow-sm no-underline hover:shadow-md transition-shadow"
              >
                <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{card.label}</div>
                <div className="text-[26px] font-semibold mt-1" style={{ color: card.color }}>{card.value}</div>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-5">
            {/* Expiring contracts */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <div className="flex justify-between items-center mb-3.5">
                <h2 className="text-sm font-semibold text-gray-700">Expiring ≤90 days</h2>
                <Link to="/reports" className="text-xs text-blue-600 hover:underline no-underline">View all →</Link>
              </div>
              {expiring.length === 0 ? (
                <p className="text-sm text-gray-400">No contracts expiring in the next 90 days.</p>
              ) : expiring.map((item, i) => (
                <div key={i} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                  <div className="min-w-0 pr-2">
                    <div className="text-sm font-medium text-gray-800 truncate">{item.vendorName}</div>
                    <div className="text-[11px] text-gray-400 truncate">{item.label}</div>
                  </div>
                  <span
                    className="px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0"
                    style={{
                      background: item.days <= 30 ? '#FCEBEB' : '#FAEEDA',
                      color:      item.days <= 30 ? '#A32D2D' : '#854F0B',
                    }}
                  >
                    {item.days}d
                  </span>
                </div>
              ))}
            </div>

            {/* Pending billing approvals */}
            <div className="bg-white rounded-xl p-5 shadow-sm">
              <div className="flex justify-between items-center mb-3.5">
                <h2 className="text-sm font-semibold text-gray-700">Billing pending approval</h2>
                <Link to="/billing" className="text-xs text-blue-600 hover:underline no-underline">View all →</Link>
              </div>
              {pendingBilling.length === 0 ? (
                <p className="text-sm text-gray-400">No billing entries pending approval.</p>
              ) : pendingBilling.map(e => {
                const vendor = vendors.find(v => v.id === e.vendorId);
                return (
                  <div key={e.id} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-0">
                    <div className="min-w-0 pr-2">
                      <div className="text-sm font-medium text-gray-800">{vendor?.name ?? 'Unknown'}</div>
                      <div className="text-[11px] text-gray-400">{e.type} · {e.entryDate}</div>
                    </div>
                    <span className="text-sm font-semibold text-gray-700 shrink-0">
                      {fmt(e.amount)}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
